import React, { useState, useEffect } from 'react';
import { sportsApi } from '../services/sportsApi';
import { Season, Sport, Country, League } from '../types/sports';
import SeasonForm from '../components/seasons/SeasonForm';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { Plus, Pencil, Trash2, Pause, Play, LayoutGrid, List, Calendar } from 'lucide-react';
import { usePreferredViewMode, STANDARD_VIEW_MODE_MAP } from '../hooks/usePreferredViewMode';
import '../styles/PageLayout.css';

/**
 * Seasons overview page with list and card view
 */
const Seasons: React.FC = () => {
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [sports, setSports] = useState<Sport[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [leagues, setLeagues] = useState<League[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = usePreferredViewMode<'list' | 'cards'>('cards', STANDARD_VIEW_MODE_MAP);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSport, setSelectedSport] = useState<number | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<number | null>(null);
  const [selectedLeague, setSelectedLeague] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingSeason, setEditingSeason] = useState<Season | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [seasonToDelete, setSeasonToDelete] = useState<Season | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [seasonsData, sportsData, countriesData, leaguesData] = await Promise.all([
        sportsApi.getSeasons(),
        sportsApi.getSports(),
        sportsApi.getCountries(),
        sportsApi.getLeagues()
      ]);
      setSeasons(seasonsData);
      setSports(sportsData);
      setCountries(countriesData);
      setLeagues(leaguesData);
    } catch (err) {
      setError('Fehler beim Laden der Saisons');
      console.error('Error loading seasons:', err);
    } finally {
      setLoading(false);
    }
  };

  // CRUD Functions
  const handleCreateSeason = () => {
    setEditingSeason(null);
    setShowForm(true);
  };

  const handleEditSeason = (season: Season) => {
    setEditingSeason(season);
    setShowForm(true);
  };

  const handleDeleteClick = async (season: Season) => {
    try {
      const result = await sportsApi.canDeleteSeason(season.id);
      if (!result.canDelete) {
        showError('Diese Saison kann nicht gelöscht werden, da sie von Wettbewerben oder Spieltagen referenziert wird.');
        return;
      }
      setSeasonToDelete(season);
      setShowDeleteConfirm(true);
    } catch (error) {
      console.error('Error checking if season can be deleted:', error);
      showError('Fehler beim Prüfen der Löschberechtigung.');
    }
  };

  const handleSaveSeason = async (data: {
    name: string;
    startDate: string;
    endDate: string;
    leagueId: number;
    isActive?: boolean;
    isCurrent?: boolean;
  }) => {
    try {
      setFormLoading(true);
      if (editingSeason) {
        await sportsApi.updateSeason(editingSeason.id, data);
        showSuccess('Saison erfolgreich aktualisiert');
      } else {
        await sportsApi.createSeason(data);
        showSuccess('Saison erfolgreich erstellt');
      }
      setShowForm(false);
      setEditingSeason(null);
      await loadData();
    } catch (err: any) {
      const errorMessage = err.message || (editingSeason ? 'Fehler beim Aktualisieren der Saison' : 'Fehler beim Erstellen der Saison');
      showError(errorMessage);
    } finally {
      setFormLoading(false);
    }
  };

  const handleToggleActive = async (season: Season) => {
    try {
      await sportsApi.toggleSeasonActive(season.id);
      showSuccess(`Saison ${season.isActive ? 'deaktiviert' : 'aktiviert'}`);
      await loadData();
    } catch (err: any) {
      showError(err.message || 'Fehler beim Ändern des Aktivitätsstatus');
    }
  };

  const confirmDelete = async () => {
    if (!seasonToDelete) return;

    try {
      await sportsApi.deleteSeason(seasonToDelete.id);
      showSuccess('Saison erfolgreich gelöscht');
      setShowDeleteConfirm(false);
      setSeasonToDelete(null);
      await loadData();
    } catch (err: any) {
      showError(err.message || 'Fehler beim Löschen der Saison');
    }
  };

  const showSuccess = (message: string) => {
    // Simple toast notification (you can replace with a proper toast library)
    alert(message);
  };

  const showError = (message: string) => {
    alert(message);
  };

  const filteredSeasons = seasons.filter(season => {
    const matchesSearch = season.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      season.league?.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSport = !selectedSport || season.league?.sport?.id === selectedSport;
    const matchesCountry = !selectedCountry || season.league?.country?.id === selectedCountry;
    const matchesLeague = !selectedLeague || season.leagueId === selectedLeague;
    
    return matchesSearch && matchesSport && matchesCountry && matchesLeague;
  });

  const filteredLeagues = leagues.filter(league => {
    const matchesSport = !selectedSport || league.sport?.id === selectedSport;
    const matchesCountry = !selectedCountry || league.country?.id === selectedCountry;
    return matchesSport && matchesCountry;
  });

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-loading">
          <div className="page-spinner"></div>
          <p>Saisons werden geladen...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="page-error">
          <h2>Fehler</h2>
          <p>{error}</p>
          <button onClick={loadData}>Erneut versuchen</button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Controls – two-row Menüband */}
      <div className="page-controls">
        {/* Row 1: Buttons */}
        <div className="page-controls-row">
          <button className="page-btn page-btn-create" onClick={handleCreateSeason}>
            <Plus size={14} /> Neu
          </button>
          <div className="page-controls-spacer" />
          <div className="page-view-toggle">
            <button className={`page-toggle-btn ${viewMode === 'cards' ? 'active' : ''}`} onClick={() => setViewMode('cards')} title="Kacheln">
              <LayoutGrid size={14} />
            </button>
            <button className={`page-toggle-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')} title="Liste">
              <List size={14} />
            </button>
          </div>
        </div>
        {/* Row 2: Search + Filters + record count */}
        <div className="page-controls-row">
          <input
            type="text"
            className="page-search-input"
            placeholder="Saisons durchsuchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <select
            className="page-filter-select"
            value={selectedSport || ''}
            onChange={(e) => {
              setSelectedSport(e.target.value ? parseInt(e.target.value) : null);
              setSelectedCountry(null);
              setSelectedLeague(null);
            }}
          >
            <option value="">Alle Sportarten</option>
            {sports.map(sport => (
              <option key={sport.id} value={sport.id}>
                {sport.icon} {sport.name}
              </option>
            ))}
          </select>
          <select
            className="page-filter-select"
            value={selectedCountry || ''}
            onChange={(e) => {
              setSelectedCountry(e.target.value ? parseInt(e.target.value) : null);
              setSelectedLeague(null);
            }}
          >
            <option value="">Alle Länder</option>
            {countries.map(country => (
              <option key={country.id} value={country.id}>
                {country.flag} {country.name}
              </option>
            ))}
          </select>
          <select
            className="page-filter-select"
            value={selectedLeague || ''}
            onChange={(e) => setSelectedLeague(e.target.value ? parseInt(e.target.value) : null)}
          >
            <option value="">Alle Ligen</option>
            {filteredLeagues.map(league => (
              <option key={league.id} value={league.id}>
                {league.name}
              </option>
            ))}
          </select>
          <div className="page-controls-spacer" />
          <span className="page-record-count">{filteredSeasons.length} / {seasons.length} Saisons</span>
        </div>
      </div>

      {/* Content */}
      {filteredSeasons.length === 0 ? (
        <div className="page-empty">
          <h3>Keine Saisons gefunden</h3>
          <p>Versuchen Sie andere Filter oder Suchbegriffe.</p>
        </div>
      ) : viewMode === 'cards' ? (
        <div className="page-cards-grid">
          {filteredSeasons.map((season) => (
            <div key={season.id} className="page-card">
              <div className="page-card-header">
                <h3 className="page-card-title">{season.name}</h3>
                <div>
                  {season.isCurrent && (
                    <span className="page-badge" style={{ background: '#fff3cd', color: '#856404' }}>Aktuell</span>
                  )}
                  <span className={`page-badge ${season.isActive ? 'page-badge-active' : 'page-badge-inactive'}`}>
                    {season.isActive ? 'Aktiv' : 'Inaktiv'}
                  </span>
                </div>
              </div>
              <div className="page-card-content">
                <div className="page-card-row"><strong>Liga:</strong> {season.league?.name || `Liga ID: ${season.leagueId}`}</div>
                <div className="page-card-row"><strong>Sportart:</strong> {season.league?.sport?.icon} {season.league?.sport?.name || 'Unbekannt'}</div>
                <div className="page-card-row"><strong>Land:</strong> {season.league?.country?.flag} {season.league?.country?.name || 'Unbekannt'}</div>
                <div className="page-card-row"><strong>Zeitraum:</strong> {new Date(season.startDate).toLocaleDateString('de-DE')} - {new Date(season.endDate).toLocaleDateString('de-DE')}</div>
                <div className="page-card-row"><strong>Erstellt:</strong> {new Date(season.createdAt).toLocaleDateString('de-DE')}</div>
              </div>
              <div className="page-card-actions">
                <button className="page-btn-icon toggle" onClick={() => handleToggleActive(season)} title={season.isActive ? "Deaktivieren" : "Aktivieren"}>
                  {season.isActive ? <Pause size={14} /> : <Play size={14} />}
                </button>
                <button className="page-btn-icon edit" onClick={() => handleEditSeason(season)} title="Bearbeiten">
                  <Pencil size={14} />
                </button>
                <button className="page-btn-icon delete" onClick={() => handleDeleteClick(season)} title="Löschen">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="page-data-panel">
          <table className="page-table">
            <thead>
              <tr>
                <th>Saison</th>
                <th>Liga</th>
                <th>Sportart</th>
                <th>Land</th>
                <th>Zeitraum</th>
                <th>Status</th>
                <th>Erstellt</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {filteredSeasons.map((season) => (
                <tr key={season.id}>
                  <td>
                    {season.name}
                    {season.isCurrent && <span className="page-badge" style={{ background: '#fff3cd', color: '#856404', marginLeft: '8px' }}>Aktuell</span>}
                  </td>
                  <td>{season.league?.name || `Liga ID: ${season.leagueId}`}</td>
                  <td>{season.league?.sport?.icon} {season.league?.sport?.name || 'Unbekannt'}</td>
                  <td>{season.league?.country?.flag} {season.league?.country?.name || 'Unbekannt'}</td>
                  <td>{new Date(season.startDate).toLocaleDateString('de-DE')} - {new Date(season.endDate).toLocaleDateString('de-DE')}</td>
                  <td>
                    <span className={`page-badge ${season.isActive ? 'page-badge-active' : 'page-badge-inactive'}`}>
                      {season.isActive ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </td>
                  <td>{new Date(season.createdAt).toLocaleDateString('de-DE')}</td>
                  <td>
                    <div className="page-table-actions">
                      <button className="page-btn-icon toggle" onClick={() => handleToggleActive(season)} title={season.isActive ? "Deaktivieren" : "Aktivieren"}>
                        {season.isActive ? <Pause size={14} /> : <Play size={14} />}
                      </button>
                      <button className="page-btn-icon edit" onClick={() => handleEditSeason(season)} title="Bearbeiten">
                        <Pencil size={14} />
                      </button>
                      <button className="page-btn-icon delete" onClick={() => handleDeleteClick(season)} title="Löschen">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Season Form Modal */}
      {showForm && (
        <SeasonForm
          season={editingSeason}
          leagues={leagues}
          onSave={handleSaveSeason}
          onCancel={() => {
            setShowForm(false);
            setEditingSeason(null);
          }}
          loading={formLoading}
        />
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && seasonToDelete && (
        <ConfirmDialog
          isOpen={showDeleteConfirm}
          title="Saison löschen"
          message={`Möchten Sie die Saison "${seasonToDelete.name}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`}
          onConfirm={confirmDelete}
          onCancel={() => {
            setShowDeleteConfirm(false);
            setSeasonToDelete(null);
          }}
          type="danger"
        />
      )}
    </div>
  );
};

export default Seasons;
